/*!

=========================================================
* Vision UI Free Chakra - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-chakra
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-chakra/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React, { useEffect, useRef, useState } from "react";
// Chakra imports
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Switch,
  Text,
  DarkMode,
} from "@chakra-ui/react";
import { NavLink } from "react-router-dom";

// Assets
import signInImage from "assets/img/signInImage.png";

// Custom Components
import AuthFooter from "components/Footer/AuthFooter";
import GradientBorder from "components/GradientBorder/GradientBorder";
import { LoaderSmall } from "components/Loader/loader";
import useApiReq from "variables/apireq";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { RingLoader } from "react-spinners";
function SignIn() {
  const titleColor = "white";
  const textColor = "gray.400";
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submit, setSubmit] = useState(false);
  const remeber = useRef(null);
  const history = useHistory();

  // Calling useApiReq here, outside the fetchLogin function
  useEffect(() => {
    if (localStorage.getItem("token")) {
      history.push(`/${localStorage.getItem("group").toLowerCase()}/dashboard`);
    }
  }, []);

  const fetchLogin = async () => {
    if (email === "" || password === "") return;
    setSubmit(true);
    setIsLoading(true);
    const res = await useApiReq({
      method: "POST",
      url: "auth/login/",
      data: {
        username: email,
        password: password,
      },
    });
    try {
      // apiResponse and apiError will automatically update based on useApiReq hook
      setResponse(res.response);
      localStorage.setItem("token", res.response.token);
      localStorage.setItem("group", res.response.user_group);
      localStorage.setItem("name", res.response.username);

      const userGroup = res.response.user_group.toLowerCase();
      const publisherGroups = ["newsroom", "publisher", "editor", "marketing"];

      const targetGroup = publisherGroups.includes(userGroup)
        ? "publisher"
        : "advertiser";

      history.push(`/${targetGroup}/dashboard`);
    } catch (err) {
      setError(res.error);
      console.log(res.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    if (savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      remeber.current.checked = true;
    }
  }, []);

  useEffect(() => {
    if (remeber.current.checked && submit) {
      localStorage.setItem("email", email);
      localStorage.setItem("password", password);
    }
  }, [email, password, submit]);

  return (
    <Flex position="relative">
      <Flex
        minH="100vh"
        h={{ base: "120vh", lg: "fit-content" }}
        w="100%"
        maxW="1044px"
        mx="auto"
        pt={{ sm: "100px", md: "0px" }}
        flexDirection="column"
        me={{ base: "auto", lg: "50px", xl: "auto" }}
      >
        <Flex
          alignItems="center"
          justifyContent="start"
          style={{ userSelect: "none" }}
          mx={{ base: "auto", lg: "unset" }}
          ms={{ base: "auto", lg: "auto" }}
          w={{ base: "100%", md: "50%", lg: "450px" }}
          px="50px"
        >
          <Flex
            direction="column"
            w="100%"
            background="transparent"
            mt={{ base: "50px", md: "150px", lg: "160px", xl: "245px" }}
            mb={{ base: "60px", lg: "95px" }}
          >
            <Heading color={titleColor} fontSize="32px" mb="10px">
              Nice to see you!
            </Heading>
            <Text
              mb="36px"
              ms="4px"
              color={textColor}
              fontWeight="bold"
              fontSize="14px"
            >
              Enter your Username and password to sign in
            </Text>
            <FormControl>
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="normal"
                color="white"
              >
                Username
              </FormLabel>
              <GradientBorder
                mb="24px"
                w={{ base: "100%", lg: "fit-content" }}
                borderRadius="20px"
              >
                <Input
                  color="white"
                  bg="rgb(19,21,54)"
                  border="transparent"
                  borderRadius="20px"
                  fontSize="sm"
                  size="lg"
                  w={{ base: "100%", md: "346px" }}
                  maxW="100%"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  h="46px"
                  placeholder="Your Username"
                />
              </GradientBorder>
            </FormControl>
            <FormControl>
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="normal"
                color="white"
              >
                Password
              </FormLabel>
              <GradientBorder
                mb="24px"
                w={{ base: "100%", lg: "fit-content" }}
                borderRadius="20px"
              >
                <Input
                  color="white"
                  bg="rgb(19,21,54)"
                  border="transparent"
                  borderRadius="20px"
                  fontSize="sm"
                  size="lg"
                  w={{ base: "100%", md: "346px" }}
                  maxW="100%"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                />
              </GradientBorder>
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <DarkMode>
                <Switch
                  id="remember-login"
                  colorScheme="brand"
                  me="10px"
                  ref={remeber}
                />
              </DarkMode>
              <FormLabel
                htmlFor="remember-login"
                mb="0"
                ms="1"
                fontWeight="normal"
                color="white"
              >
                Remember me
              </FormLabel>
            </FormControl>
            <Button
              variant="brand"
              fontSize="10px"
              type="submit"
              w="100%"
              maxW="350px"
              h="45"
              mb="20px"
              mt="20px"
              onClick={() => fetchLogin()}
            >
              {isLoading ? (
                <RingLoader size={20} color="#69C4D3" loading={true} />
              ) : (
                "Sign In"
              )}
            </Button>

            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              maxW="100%"
              mt="0px"
            >
              <Text color={textColor} fontWeight="medium">
                Don't have an account?
                <NavLink
                  to="/auth/signup"
                  color={titleColor}
                  as="span"
                  ms="5px"
                  fontWeight="bold"
                >
                  Sign Up
                </NavLink>
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Box
          w={{ base: "335px", md: "460px" }}
          mx={{ base: "auto", lg: "unset" }}
          ms={{ base: "auto", lg: "auto" }}
          mb="80px"
        >
          <AuthFooter />
        </Box>
        <Box
          display={{ base: "none", lg: "block" }}
          overflowX="hidden"
          h="100%"
          maxW={{ md: "50vw", lg: "50vw" }}
          minH="100vh"
          w="960px"
          position="absolute"
          left="0px"
        >
          <Box
            bgImage={signInImage}
            w="100%"
            h="100%"
            bgSize="cover"
            bgPosition="50%"
            position="absolute"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Text
              textAlign="center"
              color="white"
              letterSpacing="8px"
              fontSize="36px"
              fontWeight="bold"
            >
              Your gateway to impactful PR strategies
            </Text>
            <Text
              textAlign="center"
              color="white"
              letterSpacing="2px"
              fontSize="18px"
              fontWeight="500"
              mt="24px"
            >
              Access your PR content and analytics in one place. Log in to track
              performance, <br /> redefine strategies, and manage your PR
              campaigns. Dive into our <br /> data-driven insights and elevate
              your brand's message today!
            </Text>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
}

export default SignIn;
